import {AuthTypesEnum} from "@/lib/enums/AuthTypesEnum";

/**
 * Progressive-enhancement contract for the header auth CTAs (AC-729).
 *
 * The Register/Login CTAs render as real `<a href="?auth=…">` links so a tap
 * that lands before hydration still navigates instead of being silently
 * dropped. `AuthUrlOpener` reads this param on load and opens the AUTH dialog;
 * post-hydration the CTA's own `onClick` opens it directly without navigating.
 */
export const AUTH_QUERY_PARAM = "auth";

/** URL-safe values for {@link AUTH_QUERY_PARAM}. */
export enum AuthQueryValue {
    LOGIN = "login",
    REGISTER = "register",
}

/**
 * Relative href that opens the given auth tab as a real navigation fallback.
 *
 * `currentSearch` (from `useSearchParams().toString()`) is preserved and the
 * `auth` param appended, so a pre-hydration tap does NOT clobber affiliate/UTM
 * attribution (`traffic`, `ref`, `utm_*`, `tag`) — those params reach the
 * frontend only once and are captured client-side on the next load.
 */
export const authQueryHref = (
    value: AuthQueryValue,
    currentSearch?: string,
): string => {
    const params = new URLSearchParams(currentSearch);
    params.set(AUTH_QUERY_PARAM, value);
    return `?${params.toString()}`;
};

/** Maps a raw query value to its auth tab, or `null` when unrecognized. */
export const authTabFromQuery = (value: string | null): AuthTypesEnum | null => {
    switch (value) {
        case AuthQueryValue.LOGIN:
            return AuthTypesEnum.LOGIN;
        case AuthQueryValue.REGISTER:
            return AuthTypesEnum.REGISTER;
        default:
            return null;
    }
};
